import React, { useEffect, useState } from 'react';
import { Button, Toolbar } from '@mui/material';
import {
  SaveButton,
  Link,
  SimpleForm,
} from 'react-admin';
import { Row, Col, Select } from 'antd';
import { makeStyles } from '@mui/styles';
import { RcFile } from 'antd/lib/upload';
import { useController, useWatch } from 'react-hook-form';

import {
  DateInput,
  FileControlled,
  FileUpload,
  InitialCard,
  InitialPage,
  InitialRow,
  ReferenceInput,
  Scroll,
  SelectInput,
  TextAreaInput,
} from '@components';
import {
  CreditCall,
  CreditCallAnswer,
  CreditCallHistory,
  CreditFarmFile,
  DocumentFile,
} from '@types';
import { s3http } from '@network';
import { dateFormatter, showAxiosError } from '@utils';
import { CallRiskField, callRiskOptions } from '@pages/credit-call/CallRiskField';
import { CallStatusField, callStatusOptions } from '@pages/credit-call/CallStatusField';
import './call-widget.css';


interface CallWidgetProps {
  creditId?: number;
  call?: CreditCall;
  editable: boolean;
}

const CallCardHeader = ({ call }: {call?: CreditCall}) => {
  const cls = useStyles();
  if (!call) return null;
  const { credit } = call;

  return (
    <div className={cls.cardHeader}>
      {credit?.accreditedName && (
        <Link to={`/user/${credit.userId}?initial`}>
          {credit.accreditedName}
        </Link>
      )}
      {credit?.accreditedName && <span>|</span>}
      {credit && (
        <Link to={`/credit/${credit.id}?initial`}>
          {credit.code}
        </Link>
      )}
    </div>
  );
};

export const CallWidget = ({ creditId, call, editable }: CallWidgetProps) => {
  const cls = useStyles();
  const backTo = creditId ? `/credit/${creditId}?calls` : '/credit-call';
  const backText = creditId ? 'Back to task by credit' : 'All Tasks';
  const isFollowUp = call?.type === 'follow_up';

  const EditActions = () => {
    return editable ? (
      <Toolbar className={cls.toolbar}>
        <div className={cls.toolbarRow}>
          <Link to="/credit-call">
            <Button variant="outlined" color="secondary" className={cls.returnBtn}>
              Return to All Tasks
            </Button>
          </Link>
        </div>
        <div className={cls.toolbarRow}>
          <Link to={`/credit/${creditId}?calls`}>
            <Button variant="outlined" color="secondary" className={cls.returnBtn}>
              Tasks By Credit
            </Button>
          </Link>
          <SaveButton label="Save" type="button" />
        </div>
      </Toolbar>
    ) : null;
  };

  return (
    <InitialPage title="Individual task" backTo={backTo} backText={backText} gap={20}
      headerRight={<CallCardHeader call={call} />}>
      <InitialCard extraPadding contentClass={cls.content}>
        <SimpleForm toolbar={<EditActions />}>
          <Row gutter={[30, 20]} className={cls.form}>
            <Col sm={24} md={12}>
              <div className={cls.info}>
                <div className={cls.infoTitle}>{call?.title}</div>
                <CallForm call={call} creditId={creditId} />
              </div>
            </Col>
            <Col sm={24} md={12}>
              <div className={cls.history}>
                <div className={cls.title}>History</div>
                <CallHistory history={call?.history || []} />
              </div>
            </Col>
          </Row>

          {isFollowUp && (
            <Row gutter={[30, 20]} style={{ width: '100%', marginTop: 20 }}>
              <Col sm={24} md={12}>
                <div className={cls.title}>Call Log</div>
                <TextAreaInput
                  source="observation"
                  placeholder="Extra comments about the credit progress"
                  className={cls.textarea}
                />
              </Col>
              <Col sm={24} md={12}>
                <div className={cls.title}>Follow Up</div>
                <CallAnswersForm />
              </Col>
            </Row>
          )}

          {!isFollowUp && (
            <Row gutter={[30, 20]} style={{ width: '100%', marginTop: 20 }}>
              <Col sm={24} md={12}>
                <div className={cls.title}>Observation</div>
                <TextAreaInput
                  source="observation"
                  placeholder="Here, the responsible manager will add any observation made during call"
                  className={cls.textarea}
                />
              </Col>
              <Col sm={24} md={12}>
                <div className={cls.title}>Satellite Picture</div>
                <CallPicture picture={call?.picture} />
              </Col>
            </Row>
          )}

        </SimpleForm>
      </InitialCard>
    </InitialPage>
  );
};

const CallAnswersForm = () => {
  const {
    field: { value, onChange: setAnswers },
  } = useController({ name: 'answers' });

  const answers = (value || []) as CreditCallAnswer[];
  const cls = useStyles();

  return (
    <div>
      {answers.map((answer, index) => (
        <div key={answer.questionId} className={cls.question}>
          <div className={cls.questionName}>
            {index + 1}. {answer.questionName}
          </div>
          <Select
            bordered={false}
            className={cls.questionSelect}
            options={[
              ...answer.options,
              { value: '', label: '' },
            ]}
            onSelect={(val) => {
              const newAnswers = [...answers];
              newAnswers[index] = { ...newAnswers[index], answer: val };
              setAnswers(newAnswers);
            }}
            value={answer.answer}
          />
        </div>
      ))}
    </div>
  );
};

const CallForm = ({ call, creditId }: {call?: CreditCall; creditId?: number}) => {
  const [withDate, setWithDate] = useState(false);

  const cls = useStyles();
  const callStatus = useWatch({ name: 'status' });
  const callRisk = useWatch({ name: 'risk' });
  const {
    field: { onChange: setCreditId },
  } = useController({ name: 'creditId' });
  const {
    field: { value: contactAt, onChange: setContactAt },
  } = useController({ name: 'contactAt' });

  useEffect(() => {
    if (!call && creditId) {
      setTimeout(() => {
        setCreditId(creditId);
      }, 500);
    }

    if (call) {
      setWithDate(!!call.contactAt);
    }
  }, [creditId, call]);

  return (
    <div id="CallForm">
      {!call ? (
        <InitialRow title="Credit:">
          <ReferenceInput
            source="creditId"
            reference="credit/select"
            sort={{ field: 'code', order: 'ASC' }}
            perPage={999999}
            isRequired
          >
            <SelectInput optionText="code" label="" required />
          </ReferenceInput>
        </InitialRow>
      ) : null}
      <InitialRow title="Task admin:">
        <ReferenceInput
          source="responsibleAdminId"
          reference="admin/task-admin"
          isRequired
        >
          <SelectInput optionText="name" label="" required />
        </ReferenceInput>
      </InitialRow>
      <Row className={cls.manualFormRow}>
        <Col sm={24} md={11}>
          <InitialRow title="Contact by:">
            {(!call || call?.isManual) ? (
              <DateInput source="contactBy" label="" />
            ) : (
              <span>{call.contactBy && dateFormatter.toDate(call.contactBy)}</span>
            )}
          </InitialRow>
          <InitialRow title="Contact at:">
            {(!call || call?.isManual) ? (
              <>
                {(withDate || contactAt) ? (
                  <>
                    <DateInput source="contactAt" label="" />
                    <span className={cls.clearDate} onClick={() => {
                      setContactAt(null);
                      setWithDate(false);
                    }}>+</span>
                  </>
                ) : (
                  <Button className={cls.setDate} onClick={() => setWithDate(true)}>
                    Set date
                  </Button>
                )}
              </>
            ) : (
              <span>{call.contactAt && dateFormatter.toDate(call.contactAt)}</span>
            )}
          </InitialRow>
        </Col>
        <Col sm={24} md={2}></Col>
        <Col sm={24} md={11} className="CallFormSelects">
          <InitialRow title="Call status:">
            <SelectInput source="status" choices={callStatusOptions} required label="" />
            <div className="CallFormBadge">
              <CallStatusField source="risk" badgeMode callStatus={callStatus} width={100} />
            </div>
          </InitialRow>
          <InitialRow title="Risk:">
            <SelectInput source="risk" choices={callRiskOptions} required label="" />
            <div className="CallFormBadge">
              <CallRiskField source="risk" badgeMode callRisk={callRisk} width={100} />
            </div>
          </InitialRow>
        </Col>
      </Row>
    </div>
  );
};

const CallHistory = (props: {history: CreditCallHistory[]}) => {
  const cls = useStyles();
  const allItems = (props.history || []).sort((a, b) => b.id - a.id);

  return (
    <Scroll height={150}>
      {allItems.map(history => (
        <div className={cls.item} key={history.id}>
          <div className={cls.header}>
            <div className={cls.by}>by {history.adminName}</div>
            <div>{dateFormatter.toDateTime(history.createdAt, true)}</div>
          </div>
          <div className={cls.footer}>
            <div>{history.title}</div>
            {(history.statusBefore || history.statusAfter) ? (
              <div className={cls.status}>
                {(history.statusBefore || 'null').slice(0, 20)}
                <span style={{ margin: '0 4px 0' }}>→</span>
                {(history.statusAfter || 'null').slice(0, 20)}
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </Scroll>
  );
};

const CallPicture = (props: {picture?: DocumentFile | null}) => {
  const cls = useStyles();
  const [picture, setPicture] = useState<CreditFarmFile | null>(props.picture || null);
  const {
    field: { onChange: changePictureId },
  } = useController({ name: 'pictureId' });

  const uploadPicture = async (rcFile: RcFile) => {
    try {
      const file = await s3http.uploadFile(rcFile as File);
      setPicture(file);
      changePictureId(file.id);
      return file.adminUrl;
    } catch (err: any) {
      showAxiosError(err);

      return '';
    }
  };

  const removePicture = async () => {
    setPicture(null);
    changePictureId(null);
  };

  return (
    <>
      {picture ? (
        <FileControlled
          adminUrl={picture.adminUrl}
          downloadFilename={'Satellite Picture.jpg'}
          onDelete={removePicture}
          wrapClass={cls.fileWrap}
          zoom
          fullWidth
          height={200}
        />
      ) : (
        <FileUpload uploadAction={uploadPicture} className={cls.uploadBox} type="image" />
      )}
    </>
  );
};

const useStyles = makeStyles({
  question: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '40px',
  },
  questionName: {
    fontWeight: 600,
    fontSize: 14,
    width: '50%',
  },
  questionSelect: {
    marginTop: -10,
    width: 80,
    borderBottom: '1px solid var(--primary-color)',

    '& svg': {
      color: 'var(--primary-color)',
    },
  },
  cardHeader: {
    height: '100%',
    display: 'flex',
    alignItems: 'flex-end',

    '& > *': {
      marginLeft: 14,
      fontSize: 16,
      color: 'var(--primary-color)',
      fontWeight: 600,
      textDecoration: 'none',
    },
    '& > a': {
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  content: {
    paddingTop: 10,
    fontFamily: 'var(--font-primary)',
    color: 'var(--text-color)',

    '& .MuiCardContent-root': {
      marginTop: '0 !important',
    },
  },
  info: {
    padding: '18px 30px',
    borderRadius: 8,
    border: '1px solid var(--primary-color)',
    background: 'var(--background-color)',
    fontFamily: 'var(--font-primary)',
    color: 'var(--text-color)',
  },
  infoTitle: {
    fontSize: 22,
    fontWeight: 600,
  },
  form: {
    width: '100% !important',
  },
  history: {
    marginRight: -20,
    padding: '5px 30px',
    borderRadius: 8,
    border: '1px solid var(--primary-color)',
    background: 'var(--foreground-color)',
    height: '100%',
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 5,
  },
  textarea: {
    color: 'var(--text-color)',
    borderRadius: 8,
    border: '1px solid var(--logo-color)',
    minHeight: '200px!important',
    padding: '8px 12px',
    backgroundColor: 'var(--background-color)',
    fontSize: 12,
    lineHeight: 1.1,

    '> div::before': {
      display: 'none',
    },

    '&::placeholder': {
      color: 'var(--mutted-color)',
      fontSize: 12,
      lineHeight: 1.1,
    },
  },
  uploadBox: {
    height: 200,
    width: '100%',
  },
  fileWrap: {
    justifyContent: 'flex-start',
  },
  item: {
    marginBottom: 10,
    paddingRight: 10,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    color: 'var(--primary-color)',
    fontSize: 12,
    fontWeight: 400,
    borderBottom: '1px solid var(--logo-color)',
    paddingBottom: 4,
  },
  by: {},
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: 'var(--text-color)',
    fontSize: 13,
    fontWeight: 400,
  },
  status: {
    fontSize: 12,
  },
  showMoreBtn: {
    paddingLeft: 50,
    paddingRight: 50,
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 400,
  },
  toolbar: {
    justifyContent: 'space-between !important',
    padding: '15px 0 0 !important',
    '& button': {
      margin: '0 25px 0 0 !important',
      order: '0 !important',
    },
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
  },
  returnBtn: {
    textTransform: 'none',
    paddingLeft: 30,
    paddingRight: 30,
    borderRadius: 8,
    color: 'var(--primary-color) !important',
    borderColor: 'var(--primary-color) !important',
  },
  manualFormRow: {
    width: '100%',
  },
  clearDate: {
    color: 'red',
    position: 'absolute',
    transform: 'translateX(10px) rotate(45deg)',
    fontSize: 24,
    lineHeight: '15px',
    fontWeight: 300,
    cursor: 'pointer',
    zIndex: 10,
  },
  setDate: {
    position: 'relative',
    top: -10,
  },
});
